<template>
  <div class="trk-shell-header">
    <tracking-api-loader />
    <div class="trk-shell-top">
      <div>
        <div class="trk-kicker" title="Insights from patient booking journeys captured in onboarding tracking">Patient booking insights</div>
        <h1 class="trk-title" :title="titleHint || title">
          {{ title }}
          <span class="trk-new-badge">NEW</span>
        </h1>
        <p class="trk-subtitle" v-if="subtitle" :title="subtitle">{{ subtitle }}</p>
      </div>
      <div class="trk-toolbar">
        <div class="trk-date-group" v-if="showDates" :class="{ 'trk-date-group--pending': isDirty }">
          <button
            type="button"
            class="trk-period-shift"
            :title="periodShiftTitle(-1)"
            @click="shiftPeriod(-1)"
          >
            <i class="fa fa-chevron-left"></i>
          </button>
          <label for="trk-from" title="Start of the date range for this report">From</label>
          <input
            id="trk-from"
            v-model="draftFrom"
            class="form-control trk-date-input"
            type="date"
            :max="draftTo || today"
            title="Start date — UK calendar day (Europe/London)"
            @keyup.enter="apply"
          />
          <label for="trk-to" title="End of the date range for this report">To</label>
          <input
            id="trk-to"
            v-model="draftTo"
            class="form-control trk-date-input"
            type="date"
            :min="draftFrom"
            :max="today"
            title="End date — UK calendar day (Europe/London)"
            @keyup.enter="apply"
          />
          <button
            type="button"
            class="trk-period-shift"
            :title="periodShiftTitle(1)"
            :disabled="!canShiftForward"
            @click="shiftPeriod(1)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>
          <button
            class="btn trk-btn-primary trk-apply"
            type="button"
            :disabled="!isDirty || !!rangeError || showLoading"
            :title="rangeError || (isDirty ? 'Load this page for the selected dates' : 'These dates are already showing')"
            @click="apply"
          >
            <i class="fa" :class="showLoading ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            {{ showLoading ? 'Loading…' : 'Apply' }}
          </button>
        </div>
        <slot name="actions"></slot>
      </div>
    </div>

    <div class="trk-date-presets" v-if="showDates">
      <button
        v-for="preset in presets"
        :key="preset.id"
        type="button"
        class="trk-preset-chip"
        :class="{ active: isPresetActive(preset) }"
        :title="preset.hint + ' (' + preset.fromDate + ' → ' + preset.toDate + ')'"
        @click="selectPreset(preset)"
      >
        {{ preset.label }}
      </button>
      <span v-if="rangeError" class="trk-range-note trk-range-note--error">{{ rangeError }}</span>
      <span v-else-if="isDirty" class="trk-range-note">
        {{ draftSummary }} — not applied yet.
        <a href="#" @click.prevent="apply">Apply</a> ·
        <a href="#" @click.prevent="resetDraft">Cancel</a>
      </span>
      <span v-else class="trk-range-note trk-range-note--muted">Showing {{ appliedSummary }}</span>
    </div>

    <div class="trk-nav" v-if="showNav">
      <router-link
        v-for="item in nav"
        :key="item.to"
        :to="navTo(item)"
        class="trk-nav-chip"
        :class="{ 'trk-nav-chip--ai': item.ai }"
        :title="item.hint"
      >
        <i v-if="item.ai" class="fa fa-magic"></i>
        {{ item.label }}
      </router-link>
    </div>

    <!-- Additive AI chat: does not change page layout or data loading -->
    <ask-question-widget
      :page-name="title"
      :page-key="trackingPageKey"
      :page-summary="pageSummary"
      :filters="chatContext"
    />
  </div>
</template>

<script>
import {
  TRACKING_NAV,
  canShiftRangeForward,
  datePresets,
  largerDatePresets,
  rangeParams,
  shiftDateRange,
  spanDaysInclusive,
  ukTodayYmd
} from './trackingShared';

const PRESET_ORDER = ['today', 'yesterday', '7d', '14d', '30d', '90d', 'week1', 'week2', 'week3', 'week4', 'month1', 'month2'];

function allPresets() {
  const byId = {};
  datePresets().concat(largerDatePresets()).forEach((p) => { byId[p.id] = p; });
  return PRESET_ORDER.map((id) => byId[id]).filter(Boolean);
}

function shortDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[m - 1]} ${y}`;
}
import AskQuestionWidget from './AskQuestionWidget.vue';
import TrackingApiLoader from './TrackingApiLoader.vue';

export default {
  name: 'TrackingPageHeader',
  components: { AskQuestionWidget, TrackingApiLoader },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    titleHint: { type: String, default: '' },
    fromDate: { type: String, default: '' },
    toDate: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    showDates: { type: Boolean, default: true },
    showNav: { type: Boolean, default: true },
    chatFilters: { type: Object, default: () => ({}) },
    pageSummary: { type: String, default: '' }
  },
  data() {
    return {
      nav: TRACKING_NAV,
      presets: allPresets(),
      today: ukTodayYmd(),
      // Dates being chosen; the page only reloads when they are applied.
      draftFrom: this.fromDate,
      draftTo: this.toDate
    };
  },
  watch: {
    fromDate(value) { this.draftFrom = value; },
    toDate(value) { this.draftTo = value; }
  },
  computed: {
    showLoading() {
      const service = this.$onboardingTrackingService;
      const apiBusy = !!(service && service.loadingState && service.loadingState.active);
      return this.loading || apiBusy;
    },
    trackingPageKey() {
      const path = (this.$route && this.$route.path) || '';
      if (/\/tracking\/sessions\/[^/]+/.test(path)) {
        return 'session-detail';
      }
      const match = path.match(/\/tracking\/([^/]+)/);
      return match ? match[1] : 'overview';
    },
    canShiftForward() {
      return canShiftRangeForward(this.draftFrom, this.draftTo);
    },
    isDirty() {
      return this.draftFrom !== this.fromDate || this.draftTo !== this.toDate;
    },
    rangeError() {
      if (!this.draftFrom || !this.draftTo) return 'Choose a start and an end date.';
      if (this.draftFrom > this.draftTo) return 'The start date must be on or before the end date.';
      if (this.draftTo > this.today) return 'The end date cannot be in the future.';
      return '';
    },
    draftSummary() {
      return this.rangeSummary(this.draftFrom, this.draftTo);
    },
    appliedSummary() {
      return this.rangeSummary(this.fromDate, this.toDate);
    },
    chatContext() {
      const base = Object.assign({}, this.chatFilters || {});
      if (this.fromDate) {
        if (!base.from) base.from = rangeParams(this.fromDate, this.toDate || this.fromDate).from;
        if (!base.fromDate) base.fromDate = this.fromDate;
      }
      if (this.toDate) {
        if (!base.to) base.to = rangeParams(this.fromDate || this.toDate, this.toDate).to;
        if (!base.toDate) base.toDate = this.toDate;
      }
      const route = this.$route || {};
      const sessionId = route.params && route.params.sessionId;
      if (sessionId) {
        base.sessionId = base.sessionId || sessionId;
      }
      return base;
    }
  },
  methods: {
    isPresetActive(preset) {
      return this.draftFrom === preset.fromDate && this.draftTo === preset.toDate;
    },
    selectPreset(preset) {
      this.draftFrom = preset.fromDate;
      this.draftTo = preset.toDate;
    },
    resetDraft() {
      this.draftFrom = this.fromDate;
      this.draftTo = this.toDate;
    },
    /** One reload for the new range. Pages that predate `apply` still get the old events. */
    apply() {
      if (!this.isDirty || this.rangeError) {
        return;
      }
      const range = { fromDate: this.draftFrom, toDate: this.draftTo };
      if (this.$listeners.apply) {
        this.$emit('apply', range);
        return;
      }
      this.$emit('update:fromDate', range.fromDate);
      this.$emit('update:toDate', range.toDate);
      this.$nextTick(() => this.$emit('refresh'));
    },
    rangeSummary(from, to) {
      if (!from || !to) return '';
      const days = spanDaysInclusive(from, to);
      return from === to
        ? shortDate(from)
        : `${shortDate(from)} – ${shortDate(to)} (${days} days)`;
    },
    navTo(item) {
      const query = {};
      if (this.showDates && this.fromDate && this.toDate) {
        query.from = this.fromDate;
        query.to = this.toDate;
      }
      return { path: item.to, query };
    },
    periodShiftTitle(direction) {
      const span = spanDaysInclusive(this.draftFrom, this.draftTo);
      const label = span === 7 ? 'week' : `${span} days`;
      return direction < 0 ? `Previous ${label}` : `Next ${label}`;
    },
    shiftPeriod(direction) {
      if (!this.draftFrom || !this.draftTo) {
        return;
      }
      if (direction > 0 && !this.canShiftForward) {
        return;
      }
      const next = shiftDateRange(this.draftFrom, this.draftTo, direction);
      this.draftFrom = next.fromDate;
      this.draftTo = next.toDate;
    }
  }
};
</script>

<style scoped>
.trk-date-input {
  min-width: 160px;
  font-size: 15px;
  height: 40px;
}
.trk-date-group--pending {
  box-shadow: 0 0 0 2px rgba(60, 137, 105, 0.35);
  border-radius: 10px;
}
.trk-apply {
  margin-left: 6px;
  min-width: 96px;
}
.trk-nav-chip--ai {
  background: #f1faf4;
  border-color: #bfe3cc;
  color: #2f6f55;
  font-weight: 700;
}
.trk-range-note {
  align-self: center;
  margin-left: 8px;
  font-size: 0.85rem;
  color: #2f6f55;
}
.trk-range-note--muted {
  color: #7a828c;
}
.trk-range-note--error {
  color: #b3261e;
}
@media (max-width: 767px) {
  .trk-date-input {
    min-width: 0;
    flex: 1 1 130px;
  }
}
</style>
